import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import T from 'i18n-react';

import { DownIcon } from '@motorway/motorway-storybook-cra';

import Texts from 'Components/texts.json';
import { isFunction } from 'Utilities/helpers';
import useIntersectionObserver, { useSupportsIntersectionObserver } from 'Utilities/hooks/useIntersectionObserver';
import proPropTypes from 'Utilities/proPropTypes';

import styles from './BackToTop.module.scss';

T.setTexts(Texts);

const BackToTop = ({ anchorRef, buttonClassName, className, focusRef, onClick }) => {
	const [visible, setVisible] = useState(false);

	const buttonRef = useRef();
	const forwardFocusRef = useRef();
	const supportsIntersectionObserver = useSupportsIntersectionObserver();

	const onAnchorIntersect = useCallback(
		(entries) => {
			const {
				boundingClientRect: { y },
				isIntersecting,
			} = entries[0];
			const isAnchorOverScroll = y < 0; // if we scrolled over the anchor, we want to set permanent visibility

			setVisible(isIntersecting || isAnchorOverScroll);

			if (isIntersecting && forwardFocusRef.current) {
				forwardFocusRef.current = false;

				/**
				 * We must also address focus management when adding a scroll-to-top
				 * button to our sites. If we add nothing [...], the button will still
				 * have focus after it is clicked. What this means for users who navigate
				 * pages with a keyboard is that the page's focus is not where they expect it to be.
				 * They expect it to be at the top of the page [...].
				 *
				 * @source: https://ashleemboyer.com/accessible-smooth-scroll-to-top-buttons-with-little-code#managing-focus
				 */
				focusRef?.current?.focus();
			}
		},
		[focusRef],
	);

	useIntersectionObserver({
		onIntersect: onAnchorIntersect,
		target: anchorRef,
		threshold: 0,
	});

	const onButtonClick = (e) => {
		window.scrollTo({
			behavior: 'smooth',
			top: 0,
		});

		forwardFocusRef.current = true;

		if (isFunction(onClick)) {
			onClick(e);
		}
	};

	if (!supportsIntersectionObserver) {
		return null;
	}

	const label = T.translate('utilities.backToTop');

	return (
		<>
			<div className={cx(styles.backToTop, className)}>
				<button
					ref={buttonRef}
					aria-hidden={(!visible).toString()}
					aria-label={label}
					className={cx(styles.button, buttonClassName, { [styles.visible]: visible })}
					onClick={onButtonClick}
					tabIndex="0"
					type="button"
				>
					<DownIcon className={styles.icon} />
					{label}
				</button>
			</div>
		</>
	);
};

BackToTop.defaultProps = {
	anchorRef: null,
	buttonClassName: null,
	className: null,
	focusRef: null,
	onClick: null,
};

BackToTop.propTypes = {
	anchorRef: proPropTypes.forwardedRef,
	buttonClassName: proPropTypes.className,
	className: proPropTypes.className,
	focusRef: proPropTypes.forwardedRef,
	onClick: PropTypes.func,
};

export default BackToTop;
